import { useMemo, useState } from "react";

import clsx from "clsx";
import FuzzySearch from "fuzzy-search";
import { groupBy, orderBy } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { NumberInput } from "@components/v2";
import { FilterButtonSelect } from "@features/metrics-explorer/components/filter-button-select";
import { QuickFilterButton } from "@features/metrics-explorer/components/quick-filter-button";
import { useQuickFilterOptions } from "@features/metrics-explorer/hooks";
import {
  getExcludedFrequentlyUsedMetrics,
  getFrequentlyUsedMetrics,
} from "@features/overview-v2/utils";
import { FilterButtonActive } from "@features/sales-v3/top-level-filters/components/filter-button-active";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { Modal } from "@shared/modal";
import {
  AttributionModelSelect,
  Select,
  SelectWithGroupedOptions,
} from "@shared/selects";

import { KPI_FILTER_EQUATION_LABELS } from "../../constants";
import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";
import { type KpiFilterEquation, type MetricFilter } from "../../types";
import { getNumberInputIconForMetric } from "../../utils";

interface Props {
  isOpen: boolean;
  onClose(): void;
  onSubmit(value: {
    metricId: string;
    equation: KpiFilterEquation;
    value: number;
    filters: MetricFilter[];
    attributionModel: string;
    accountingMode: INBAccountingMode;
    attributionWindow: IMCAttributionWindowDays;
  }): void;
  onDelete?(): void;
  initialMetricId?: string;
  initialEquation?: KpiFilterEquation;
  initialValue?: number;
  initialFilters?: MetricFilter[];
  initialAttributionModel?: string;
  initialAttributionWindow?: IMCAttributionWindowDays;
  initialAccountingMode?: INBAccountingMode;
}

export const MetricModal = (props: Props) => {
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      titleContent={<div className="font-bold text-xl">Add Metric</div>}
    >
      {props.isOpen ? <Content {...props} /> : <div className="h-[30vh]"></div>}
    </Modal>
  );
};

const Content = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialMetricId,
  initialEquation,
  initialValue,
  initialFilters,
  initialAttributionModel,
  initialAttributionWindow,
  initialAccountingMode,
}: Props) => {
  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const { buildKpiMetrics } = useUniversalBenchmarksConfiguratorContext();

  const [metricId, setMetricId] = useState<string | null>(
    initialMetricId ?? null,
  );
  const [equation, setEquation] = useState<KpiFilterEquation>(
    initialEquation ?? "gt",
  );
  const [value, setValue] = useState<number>(initialValue ?? 0);
  const [filters, setFilters] = useState<MetricFilter[]>(initialFilters ?? []);
  const [attributionModel, setAttributionModel] = useState(
    initialAttributionModel ?? "northbeam_custom",
  );
  const [attributionWindow, setAttributionWindow] =
    useState<IMCAttributionWindowDays>(initialAttributionWindow ?? "infinity");
  const [accountingMode, setAccountingMode] = useState<INBAccountingMode>(
    initialAccountingMode ?? "cash",
  );

  const [aboutToDelete, setAboutToDelete] = useState(false);

  const metrics = useMemo(() => {
    return buildKpiMetrics(attributionModel, attributionWindow, accountingMode);
  }, [attributionModel, attributionWindow, accountingMode, buildKpiMetrics]);

  const frequentlyUsedMetrics = getFrequentlyUsedMetrics(metrics);
  const otherMetrics = orderBy(
    getExcludedFrequentlyUsedMetrics(metrics),
    "name",
  );

  const metric = useMemo(
    () => metrics.find(({ id }) => id === metricId),
    [metrics, metricId],
  );

  const topLevelFilters = useMemo(() => {
    const grouped = groupBy(filters, "breakdown");
    return Object.entries(grouped).map(([breakdown, values]) => ({
      key: breakdown,
      values: values.map((e) => e.value),
    }));
  }, [filters]);

  const quickFilterOptions = useQuickFilterOptions();

  const visibleQuickFilterOptions = quickFilterOptions.filter(
    (e) =>
      !filters.find(
        (filter) =>
          filter.breakdown === e.breakdown && filter.value === e.value,
      ),
  );

  return (
    <div>
      <div className="mx-0.5 -mt-1 space-y-4">
        <div>
          <label className="mb-1.5 font-bold text-[11px]">
            FILTERS (OPTIONAL)
          </label>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {visibleQuickFilterOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  {visibleQuickFilterOptions.map((e) => (
                    <QuickFilterButton
                      key={e.value}
                      filter={e}
                      onClick={() => {
                        const newFilters = [...filters, { ...e }];
                        setFilters(newFilters);
                      }}
                    />
                  ))}
                </div>
              )}
              <FilterButtonSelect
                breakdownConfigs={breakdownConfigs}
                breakdowns={topLevelFilters}
                onChange={(values) => {
                  const newFilters = values.flatMap((breakdown) =>
                    breakdown.values.map((value) => ({
                      breakdown: breakdown.key,
                      value,
                    })),
                  );

                  setFilters(newFilters);
                }}
              />
            </div>
            {topLevelFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {topLevelFilters.map((e) => (
                  <FilterButtonActive
                    key={e.key}
                    breakdown={e}
                    tooltipPlacement="left"
                    onDelete={() => {
                      const newFilters = filters.filter(
                        (v) => v.breakdown !== e.key,
                      );

                      setFilters(newFilters);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <SelectWithGroupedOptions
              label={<span className="font-bold text-[11px]">METRIC</span>}
              className={clsx(
                "focus:outline-none focus:ring-cosmic focus:border-cosmic focus:ring-1",
                "focus:ring-inset focus-within:outline-none focus-within:ring-inset",
              )}
              menuClassName="z-[9998] max-h-[20rem]"
              CustomSearchClass={FuzzySearch}
              onChange={(metric) => {
                setMetricId(metric ? metric.id : null);
              }}
              isSearchable
              value={metric}
              groupedOptions={[
                {
                  label: "Frequently Used",
                  options: frequentlyUsedMetrics.map((metric) => ({
                    label:
                      metric.metadata.overviewPageMetricName ||
                      metric.metadata.name ||
                      metric.name,
                    value: metric,
                  })),
                },
                {
                  options: otherMetrics.map((metric) => ({
                    label:
                      metric.metadata.overviewPageMetricName ||
                      metric.metadata.name ||
                      metric.name,
                    value: metric,
                  })),
                },
              ]}
            />
          </div>
          <div>
            <AttributionModelSelect
              isDisabled={metric?.spec?.id.startsWith("forecasted")}
              label={
                <span className="font-bold text-[11px]">ATTRIBUTION MODEL</span>
              }
              attributionModel={attributionModel}
              onChange={(value) => {
                setAttributionModel(value);
              }}
            />
            {metric?.spec?.id.startsWith("forecasted") && (
              <div className="text-xxs text-muted mt-1">
                Attribution model can't be changed for forecasted metric
              </div>
            )}
          </div>
          <div>
            <NbAccountingModeSelect
              label={
                <span className="font-bold text-[11px]">ACCOUNTING MODE</span>
              }
              nbAccountingMode={accountingMode}
              isLoading={false}
              isDisabled={
                metric?.spec?.group === "custom" ||
                metric?.spec?.id.startsWith("forecasted")
              }
              icon={null}
              onChange={(value) => {
                setAccountingMode(value);
                setMetricId(null);

                if (value !== "accrual") {
                  setAttributionWindow("infinity");
                }
              }}
            />
          </div>
          {accountingMode === "accrual" && (
            <div>
              <AttributionWindowSelect
                label={
                  <span className="font-bold text-[11px]">
                    ATTRIBUTION WINDOW
                  </span>
                }
                attributionModel={attributionModel}
                attributionWindow={attributionWindow}
                timeGranularity="daily"
                onChange={(value) => {
                  setAttributionWindow(value);
                }}
              />
            </div>
          )}
          <div>
            <Select
              label={<span className="font-bold text-[11px]">EQUATION</span>}
              className={clsx(
                "focus:outline-none focus:ring-cosmic focus:border-cosmic focus:ring-1",
                "focus:ring-inset focus-within:outline-none focus-within:ring-inset",
              )}
              menuClassName="z-[9998] max-h-[20rem]"
              onChange={(val: KpiFilterEquation) => {
                setEquation(val);
              }}
              value={equation}
              options={[
                { label: KPI_FILTER_EQUATION_LABELS["gt"], value: "gt" },
                { label: KPI_FILTER_EQUATION_LABELS["lt"], value: "lt" },
              ]}
            />
          </div>
          <div>
            <label className="mb-1.5 font-bold text-[11px]">VALUE</label>
            <div>
              <NumberInput
                wrapperClassName="w-full"
                leftIcon={getNumberInputIconForMetric(user, metric)}
                value={metric?.format === "percentage" ? value * 100 : value}
                onChange={(val) => {
                  if (val) {
                    setValue(metric?.format === "percentage" ? val / 100 : val);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="border-b border-gray-1"></div>
        {aboutToDelete ? (
          <div
            className={clsx(
              "bg-mars-2 border border-mars-1 rounded-lg py-[9px] px-4",
              "flex items-center justify-between mt-[-18px]",
            )}
          >
            <h3 className="text-sm font-bold mb-0 relative">Delete metric?</h3>
            <div className="flex items-center gap-2">
              <PrimaryButton
                className="font-bold text-xs bg-mars"
                onClick={() => {
                  onDelete?.();
                }}
              >
                YES, DELETE
              </PrimaryButton>
              <SecondaryButton
                className="font-bold text-xs"
                onClick={() => setAboutToDelete(false)}
              >
                NO, CANCEL
              </SecondaryButton>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {initialMetricId && (
                <TertiaryButton
                  className="font-bold flex items-center gap-2"
                  onClick={() => setAboutToDelete(true)}
                >
                  <i className={clsx("fa-regular fa-trash")} />
                  DELETE
                </TertiaryButton>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <TertiaryButton className="font-bold" onClick={() => onClose()}>
                CANCEL
              </TertiaryButton>
              <PrimaryButton
                className="font-bold flex items-center gap-2"
                disabled={!metricId}
                onClick={() => {
                  if (metricId) {
                    onSubmit({
                      metricId,
                      equation,
                      value,
                      filters,
                      attributionModel,
                      attributionWindow,
                      accountingMode,
                    });
                  }
                }}
              >
                {initialMetricId ? "UPDATE" : "ADD METRIC"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
